import React, { useContext, useRef, useState } from "react";
import { ReactComponent as WhatsappIcon } from "../../assets/icons/whatsapp-filled.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/mail-filled.svg";
import { ReactComponent as QuestionIcon } from "../../assets/icons/question-filled.svg";
import FloatingLabelInput from "../../Components/FloatingLabelInput";
import axios from "axios";
import { NotificationContext } from "../../Context/NotificationContext";
import { DataContext } from "../../Context/DataContext";
import { useScreenTransition } from "../../Hooks/useScreenTransition";
import { getFullHttpUrl } from "../../firebase.config";
import { EAST1, EAST4 } from "../../utility/enums";
import { useTurnstile, Honeypot, honeypotValue } from "../../Components/Turnstile";

// Something@something.something with no spaces. The old pattern also turned
// away real addresses, like "juan@firm.co.uk" or "maria+cases@lawfirm.com".
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Top to bottom, so a failed submit can focus the first field to fix.
const FIELD_ORDER = ["name", "businessName", "email", "phone", "expectedVolume", "message"];

const SupportSection = ({ windowSize }) => {
  const { showNotification } = useContext(NotificationContext);
  const { userData } = useContext(DataContext);

  const { transitionTo } = useScreenTransition();

  const [submitButton, setSubmitButton] = useState("Submit");
  // One widget for both choices: its action is "contact", which
  // businessFormSubmitted accepts too.
  const human = useTurnstile("contact");
  const honeypotRef = useRef(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [businessType, setBusinessType] = useState("person");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [expectedVolume, setExpectedVolume] = useState("");

  // Errors show once a field has been left, or after a submit attempt, so the
  // form does not greet a first-time visitor with red text.
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const getErrors = () => {
    const errors = {};
    if (name.trim() === "") errors.name = "Please enter your name.";
    if (email.trim() === "") {
      errors.email = "Please enter your email address.";
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      errors.email = "Please enter a valid email address, like name@firm.com.";
    }
    if (message.trim() === "") {
      errors.message = businessType === "business"
        ? "Please describe your translation needs."
        : "Please enter a message.";
    }
    if (businessType === "business") {
      if (businessName.trim() === "") errors.businessName = "Please enter your business name.";
      if (phone.trim() === "") errors.phone = "Please enter a phone number.";
      if (expectedVolume.trim() === "") errors.expectedVolume = "Please enter your expected volume.";
    }
    return errors;
  };

  const errors = getErrors();
  const fieldError = (field) => (touched[field] || submitAttempted) ? errors[field] : undefined;
  const markTouched = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmitClick = () => {
    if (submitButton === "Submitting...") {
      return;
    }
    const firstInvalid = FIELD_ORDER.find((field) => errors[field]);
    if (firstInvalid) {
      setSubmitAttempted(true);
      const input = document.getElementById(`contact-${firstInvalid}`);
      if (input) input.focus();
      return;
    }
    handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitButton("Submitting...");

    const data = {
      name: name,
      email: email.trim().toLowerCase(),
      message: message,
      website: honeypotValue(honeypotRef),
    }
    if (businessType === "business") {
      data.businessName = businessName;
      data.phone = phone;
      data.expectedVolume = expectedVolume;
      data.translationNeeds = message;
    }
    data.turnstileToken = await human.token();

    axios({
      method: "post",
      url: getFullHttpUrl(businessType === "business" ? "businessFormSubmitted" : "contactFormSubmitted", EAST4),
      data: {
        ...data,
      },
    })
      .finally(() => human.reset())
      .then((response) => {
        setName("");
        setEmail("");
        setMessage("");
        setBusinessName("");
        setPhone("");
        setExpectedVolume("");
        setTouched({});
        setSubmitAttempted(false);
        setSubmitButton("Submit");
        showNotification(
          "Success",
          "Your message was successfully sent to our Support team.",
          "success"
        );
      })
      .catch((error) => {
        setSubmitButton("Submit");
        showNotification(
          "Error",
          "An error ocurred sending your message. Please, try again. If it persists, contact technical support at support@everbary.com",
          "error"
        );
      });
  };


  return (
    <div
      className="w-full grid gap-5 md:grid-cols-2 grid-cols-1 mt-5"
      id="support"
    >
      <div className="bg-white rounded-4xl p-8 shadow-md ">
        <div className="flex flex-col gap-4">
          <h2 className="md:text-5xl text-4xl text-tertiary font-medium uppercase">
            Support
          </h2>
          <h2 className="text-2xl font-medium mt-4">
            8 AM - 11 PM ET
            {windowSize.width < 1024 ? <br /> : " "}
            <span className="text-[#686F7B] text-base font-normal">
              (After hours for urgent matters)
            </span>
          </h2>
        </div>
        <div className="flex flex-col gap-4 mt-4">
          <button className="text-primary text-base font-medium flex gap-2 items-center" onClick={() =>
            window.open(
              "mailto:support@everbary.com",
              "_blank"
            )
          }>
            {/* translate="no": the browser's translation turned it into "soporte@everbary.com". */}
            <MailIcon className="h-6 w-6" /> <span translate="no">support@everbary.com</span>
          </button>
          {/* The note runs on from "WhatsApp" as text: as a flex item of its own
              it broke into a centered block next to it on a phone, mostly once
              a browser translated it into something longer. */}
          <button
            className="text-primary text-base font-medium flex gap-2 items-start text-left"
            onClick={() => {
              const url = "https://wa.me/15059168399/?text=Hello!%20I%20am%20reaching%20out%20to%20you%20because%20";
              // The ad reference, as on a clicked link (tools/visit-origin.js).
              window.open(typeof window.evWaHref === "function" ? window.evWaHref(url) : url, "_blank");
            }}
          >
            <WhatsappIcon className="h-6 w-6 flex-none" />
            <span className="leading-6">
              WhatsApp{" "}
              <span className="text-[#686F7B] text-base font-normal">
                {windowSize.width < 1024
                  ? "(For faster replies)"
                  : "(Recommended for faster replies)"}
              </span>
            </span>
          </button>
        </div>
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-medium mt-4">Useful links</h2>
          <button
            className="text-primary text-base font-medium flex gap-2 items-center"
            onClick={() => transitionTo("/faq")}
          >
            <QuestionIcon className="h-6 w-6" />
            F.A.Q.
          </button>
        </div>
      </div>

      <div className="bg-tertiary rounded-4xl p-8 relative" onFocus={human.start}>
        <Honeypot inputRef={honeypotRef} />
        {/* h2, not h1: this block is shared by nine pages, and each of them
            already has its own h1. The size comes from the classes, so it looks
            exactly the same. */}
        <h2 className="md:text-5xl text-4xl text-white font-medium uppercase mb-6">
          Contact us
        </h2>
        <div className="flex flex-col space-y-4 ">
          <select className="block p-3 w-full text-base text-white bg-tertiary border border-gray-700 rounded-md focus:outline-none focus:ring-0 peer placeholder-transparent"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
          >
            <option value="person">Person</option>
            <option value="business">Business</option>
          </select>
          <FloatingLabelInput
            type={"text"}
            id="contact-name"
            label={"Name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={markTouched("name")}
            error={fieldError("name")}
          />
          {businessType === "business" && <FloatingLabelInput
            type={"text"}
            id="contact-businessName"
            label={"Business Name"}
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            onBlur={markTouched("businessName")}
            error={fieldError("businessName")}
          />}
          <FloatingLabelInput
            type={"text"}
            id="contact-email"
            label={"Email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={markTouched("email")}
            error={fieldError("email")}
          />
          {businessType === "business" && <FloatingLabelInput
            type={"text"}
            id="contact-phone"
            label={"Phone"}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={markTouched("phone")}
            error={fieldError("phone")}
          />}
          {businessType === "business" && <FloatingLabelInput
            type={"text"}
            id="contact-expectedVolume"
            label={"Expected Volume"}
            value={expectedVolume}
            onChange={(e) => setExpectedVolume(e.target.value)}
            onBlur={markTouched("expectedVolume")}
            error={fieldError("expectedVolume")}
          />}
          <div className="w-full">
          <div className="relative w-full">
            <textarea
              id="contact-message"
              placeholder=" "
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={markTouched("message")}
              aria-invalid={fieldError("message") ? true : undefined}
              aria-describedby={fieldError("message") ? "contact-message-error" : undefined}
              className={`block p-3 pt-5 w-full text-base text-white bg-tertiary border ${fieldError("message") ? "border-red-400" : "border-gray-700"} rounded-md focus:outline-none focus:ring-0 peer placeholder-transparent`}
            />
            <label
              htmlFor="contact-message"
              className="absolute text-base text-white duration-300 transform -translate-y-4 bg-tertiary top-2 left-3 z-10 origin-[0] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:-translate-y-5"
            >
              {businessType === "business" ? "Describe your translation needs" : "Message"}
            </label>
          </div>
          {fieldError("message") && (
            <p id="contact-message-error" className="mt-1 text-sm text-red-300">
              {fieldError("message")}
            </p>
          )}
          </div>

          <div ref={human.boxRef} className="empty:hidden" />
          <button
            onClick={handleSubmitClick}
            disabled={submitButton === "Submitting..."}
            className="mt-4 p-3 text-white font-semibold rounded-full transition-colors bg-primary hover:bg-cyan-600"
          >
            {submitButton !== "Submitting..." ? submitButton : "Submitting, Please wait"}
            {submitButton === "Submitting..." && <span className="dotsLoading">...</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportSection;
